import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { XIcon } from "./icons";

/**
 * Kit modal (§2.7): focused create/confirm only — one screen, never nested.
 * Esc and overlay-click close. Anything long belongs in a Drawer or a page.
 */
function Modal({ open, onClose, title, footer, children }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (dialogRef.current) {
      dialogRef.current.focus();
    }
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="fk-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="fk-modal"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="fk-modal__header">
          <h2 className="fk-modal__title">{title}</h2>
          <button type="button" className="fk-icon-btn" onClick={onClose} aria-label="Close">
            <XIcon />
          </button>
        </header>
        <div className="fk-modal__body">{children}</div>
        {footer ? <footer className="fk-modal__footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
